import { useState, useRef, useCallback } from 'react';
import {
  PLACE_SEARCH_MIN_CHARS,
  parsePlaceResult,
  searchPlaces,
} from '../../services/placeSearchService';

const DEBOUNCE_MS = 350;

/**
 * Búsqueda geográfica vía Nominatim (OpenStreetMap).
 * @param {{
 *   disabled?: boolean,
 *   skipGeocode?: ((query: string) => boolean) | null,
 * }} options
 */
export function usePlaceSearch({ disabled = false, skipGeocode = null } = {}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [resultsQuery, setResultsQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  const debounceTimer = useRef(null);
  const abortController = useRef(null);

  const search = useCallback(async (q) => {
    if (disabled) return;
    if (typeof skipGeocode === 'function' && skipGeocode(q)) {
      if (abortController.current) abortController.current.abort();
      setResults([]);
      setResultsQuery('');
      setError(null);
      setLoading(false);
      return;
    }
    if (q.trim().length < PLACE_SEARCH_MIN_CHARS) {
      setResults([]);
      setResultsQuery('');
      setError(null);
      setLoading(false);
      return;
    }

    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const hits = await searchPlaces(q, { signal: abortController.current.signal });
      setResults(hits);
      setResultsQuery(q);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError('No se pudo conectar al servicio de búsqueda.');
      setResults([]);
      setResultsQuery('');
    } finally {
      setLoading(false);
    }
  }, [disabled, skipGeocode]);

  const handleQueryChange = useCallback((val) => {
    setQuery(val);
    setHoveredIndex(-1);
    if (disabled) return;
    clearTimeout(debounceTimer.current);
    if (typeof skipGeocode === 'function' && skipGeocode(val)) {
      if (abortController.current) abortController.current.abort();
      setResults([]);
      setResultsQuery('');
      setError(null);
      setLoading(false);
      return;
    }
    debounceTimer.current = setTimeout(() => search(val), DEBOUNCE_MS);
  }, [disabled, search, skipGeocode]);

  const clearQuery = useCallback(() => {
    setQuery('');
    setResults([]);
    setResultsQuery('');
    setError(null);
    setHoveredIndex(-1);
  }, []);

  return {
    query,
    results,
    resultsQuery,
    loading,
    error,
    hoveredIndex,
    setHoveredIndex,
    handleQueryChange,
    clearQuery,
    parseResult: parsePlaceResult,
    minChars: PLACE_SEARCH_MIN_CHARS,
  };
}
